import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { TreeSelectProps, Id, TreeNode } from './types'
import { saveSelectionSingle, loadChildren } from './api'
import { useTreeData, idKey } from './useTreeData'

const Item: React.FC<{
	node: TreeNode
	level: number
	expandedIds: Set<string>
	onToggle: (n: TreeNode) => Promise<void> | void
	onPick: (n: TreeNode) => Promise<void> | void
	loadingIds: Set<string>
	selected: Array<Id>
	leafOnly: boolean
}> = ({ node, level, expandedIds, onToggle, onPick, loadingIds, selected, leafOnly }) => {
	const expanded = expandedIds.has(idKey(node.id))
	const isLoading = loadingIds.has(idKey(node.id))
	const isNonSelectable = leafOnly && node.hasChildren
	return (
		<div className="select-none">
			<div
				className={`flex items-center gap-2 py-1.5 px-2 hover:bg-slate-50 rounded-md transition-colors ${isNonSelectable ? 'cursor-default text-slate-500' : 'cursor-pointer'}`}
				style={{ paddingLeft: `${level * 16 + 8}px` }}
				onClick={() => onPick(node)}
			>
				{node.hasChildren ? (
					<button
						type="button"
						className={`flex items-center justify-center w-5 h-5 text-slate-600 hover:text-slate-800 rounded transition-transform ${expanded ? 'rotate-90' : ''}`}
						onClick={(e) => {
							e.stopPropagation()
							void onToggle(node)
						}}
						title={expanded ? 'Collapse' : 'Expand'}
					>
						<i className="fa-solid fa-chevron-right text-[10px]"></i>
					</button>
				) : (
					<span className="inline-block w-5" />
				)}
				<span
					className={`truncate ${selected.includes(node.id) ? 'text-blue-600 font-medium' : ''}`}
					title={node.title}
					style={node.color ? { color: node.color } : {}}
				>
					{node.color && (
						<span className="inline-block w-2 h-2 rounded-full mr-2" style={{ backgroundColor: node.color }}></span>
					)}
					{node.title}
				</span>
			</div>
			{expanded && isLoading && (
				<div className="pl-6 py-1 text-slate-500 text-xs" style={{ paddingLeft: `${(level + 1) * 16 + 8}px` }}>
					<i className="fa-solid fa-spinner fa-spin mr-2"></i>
					Loading...
				</div>
			)}
			{expanded && node.children && node.children.length > 0 && (
				<div className="ml-4">
					{node.children.map((c) => (
						<div key={idKey(c.id)} className="border-b border-gray-100">
							<Item
								node={c}
								level={level + 1}
								expandedIds={expandedIds}
								onToggle={onToggle}
								onPick={onPick}
								loadingIds={loadingIds}
								selected={selected}
								leafOnly={leafOnly}
							/>
						</div>
					))}
				</div>
			)}
		</div>
	)
}

const SingleTreeSelect: React.FC<TreeSelectProps> = (props) => {
	const {
		endpoint,
		valueKey,
		isSearchable,
		defaultValue,
		defaultTitle,
		placeholder,
		inputId,
		preloadedData,
		preloadedActive,
		leafOnly = false,
	} = props
	const backend = useMemo(() => ({ endpoint, valueKey }), [endpoint, valueKey])

	const {
		root,
		setRoot,
		nodes,
		indexNodes,
		expandedIds,
		setExpandedIds,
		loadingIds,
		ensureChildrenLoaded,
		pending,
		setPending,
		search,
		setSearch,
	} = useTreeData({
		backend,
		isSearchable,
		preloadedData,
		preloadedActive,
		defaultValue,
		defaultTitle,
	})

	const [open, setOpen] = useState<boolean>(false)
	const containerRef = useRef<HTMLDivElement | null>(null)
	const triggerRef = useRef<HTMLDivElement | null>(null)
	const [dropdownStyles, setDropdownStyles] = useState<React.CSSProperties>({})
	const [selected, setSelected] = useState<Array<Id>>(
		Array.isArray(defaultValue)
			? defaultValue
			: defaultValue !== null && defaultValue !== undefined
				? [defaultValue]
				: [],
	)
	const [titles, setTitles] = useState<Array<string>>(
		Array.isArray(defaultTitle) ? defaultTitle : defaultTitle ? [defaultTitle] : [],
	)

	// Sync with props when they change (e.g. after AJAX redraw)
	useEffect(() => {
		const newSelected = Array.isArray(defaultValue)
			? defaultValue
			: defaultValue !== null && defaultValue !== undefined
				? [defaultValue]
				: []
		setSelected(newSelected)

		const newTitles = Array.isArray(defaultTitle) ? defaultTitle : defaultTitle ? [defaultTitle] : []
		setTitles(newTitles)
	}, [defaultValue, defaultTitle])

	// Sync hidden input
	useEffect(() => {
		const input = document.getElementById(inputId) as HTMLInputElement | null
		if (!input) return
		input.value = selected[0]?.toString() ?? ''
	}, [inputId, selected])

	const onToggle = useCallback(
		async (node: TreeNode) => {
			const k = idKey(node.id)
			const willExpand = !expandedIds.has(k)
			setExpandedIds((prev) => {
				const next = new Set(prev)
				next.has(k) ? next.delete(k) : next.add(k)
				return next
			})
			if (willExpand && !node.loaded) {
				await ensureChildrenLoaded(node)
			}
		},
		[expandedIds, ensureChildrenLoaded, setExpandedIds],
	)

	const onPick = useCallback(
		async (node: TreeNode) => {
			if (leafOnly && node.hasChildren) {
				void onToggle(node)
				return
			}
			setSelected([node.id])
			setTitles([node.title])
			setOpen(false)
			await saveSelectionSingle(backend, valueKey, node.id)
		},
		[backend, valueKey, leafOnly, onToggle],
	)

	// Load root on open when not present and not searching and no preload set root
	useEffect(() => {
		let canceled = false
		if (!open) return
		if (root.length > 0 && search === '') return
		if (search !== '') return
		setPending(true)
		loadChildren(backend, undefined)
			.then((data) => {
				if (canceled) return
				const seeded = data.map((n) => ({ ...n, children: n.children ?? [] }))
				indexNodes(seeded)
				setRoot(seeded)
			})
			.finally(() => setPending(false))
		return () => {
			canceled = true
		}
	}, [backend, indexNodes, open, root.length, search, setPending, setRoot])

	// Close on outside click
	useEffect(() => {
		if (!open) return
		const onDocMouseDown = (e: MouseEvent) => {
			const target = e.target as Node | null
			if (!containerRef.current) return
			if (target && containerRef.current.contains(target)) return
			// Check if target is inside the portal dropdown
			const portalDropdown = document.getElementById('tree-select-portal-dropdown')
			if (target && portalDropdown?.contains(target)) return
			setOpen(false)
		}
		document.addEventListener('mousedown', onDocMouseDown)
		return () => document.removeEventListener('mousedown', onDocMouseDown)
	}, [open])

	useLayoutEffect(() => {
		if (!open || !triggerRef.current) return

		const updatePosition = () => {
			if (!triggerRef.current) return
			const rect = triggerRef.current.getBoundingClientRect()
			const dropdownMaxHeight = 450
			const gap = 8
			const spaceBelow = window.innerHeight - rect.bottom - gap
			const spaceAbove = rect.top - gap
			const shouldOpenAbove = spaceBelow < dropdownMaxHeight && spaceAbove > spaceBelow

			const styles: React.CSSProperties = {
				position: 'fixed',
				left: `${rect.left}px`,
				width: `${rect.width}px`,
				zIndex: 9999,
			}

			if (shouldOpenAbove) {
				const availableHeight = Math.min(dropdownMaxHeight, spaceAbove)
				styles.bottom = `${window.innerHeight - rect.top + gap}px`
				styles.maxHeight = `${availableHeight}px`
			} else {
				const availableHeight = Math.min(dropdownMaxHeight, spaceBelow)
				styles.top = `${rect.bottom + gap}px`
				styles.maxHeight = `${availableHeight}px`
			}

			setDropdownStyles(styles)
		}

		updatePosition()
		window.addEventListener('scroll', updatePosition, true)
		window.addEventListener('resize', updatePosition)

		return () => {
			window.removeEventListener('scroll', updatePosition, true)
			window.removeEventListener('resize', updatePosition)
		}
	}, [open])

	return (
		<div ref={containerRef} className="p-2">
			<div className="relative min-h-10">
				<div ref={triggerRef} className="relative w-full min-h-10">
					{pending && root.length === 0 && (
						<div className="absolute inset-0 m-auto z-20 grid place-items-center bg-white/80 backdrop-blur-sm select-none pointer-events-none rounded-lg">
							<div className="flex items-center gap-2">
								<div className="w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
								<span className="text-sm text-gray-600 font-medium">Loading...</span>
							</div>
						</div>
					)}

					<div
						onClick={() => setOpen((v) => !v)}
						className={`my-2 transition-all duration-200 bg-white border-2 w-full min-h-12 border-gray-200 hover:border-gray-300 pl-4 pr-12 py-3 text-sm focus:border-blue-500 focus:ring-4 focus:ring-blue-100 text-left cursor-pointer rounded-lg shadow-sm hover:shadow-md ${open ? 'border-blue-500 ring-4 ring-blue-100 shadow-md' : ''}`}
						tabIndex={0}
					>
						<div className="flex items-center min-h-6">
							{titles[0] ? (
								<div
									className={`inline-flex items-center px-3 py-1.5 rounded-lg text-sm font-medium border shadow-sm transition-all duration-150 max-w-full ${
										nodes[idKey(selected[0])]?.color
											? ''
											: 'bg-gradient-to-r from-blue-50 to-blue-100 text-blue-700 border-blue-200'
									}`}
									style={
										nodes[idKey(selected[0])]?.color
											? {
													backgroundColor: `${nodes[idKey(selected[0])].color}1a`, // 10% opacity
													color: nodes[idKey(selected[0])].color,
													borderColor: `${nodes[idKey(selected[0])].color}4d`, // 30% opacity
												}
											: {}
									}
								>
									<span className="truncate">{titles[0]}</span>
								</div>
							) : (
								<span className="truncate text-slate-500">{placeholder ?? 'Select item...'}</span>
							)}
						</div>
					</div>
					<button
						type="button"
						className={`transition size-4 absolute z-10 top-3 right-2 my-auto flex items-center focus:outline-none ${open ? '-rotate-180' : ''}`}
						onClick={() => setOpen((v) => !v)}
					>
						<i className="block fa-solid fa-chevron-down text-xs text-slate-900"></i>
					</button>
				</div>
			</div>

			{open &&
				createPortal(
					<div
						id="tree-select-portal-dropdown"
						style={dropdownStyles}
						className="bg-white shadow-2xl border-2 border-gray-200 flex flex-col rounded-lg overflow-hidden"
					>
						{isSearchable && (
							<div className="flex items-center px-4 border-b border-slate-300 w-full">
								<div className="relative w-full">
									<input
										type="text"
										className="transition block bg-slate-100 border border-slate-300 w-full h-8 pl-2 pr-3 text-[10px] focus:border-slate-900 placeholder:text-slate-400"
										placeholder="Search..."
										value={search}
										onChange={(e) => setSearch(e.target.value)}
									/>
								</div>
							</div>
						)}
						<div className="overflow-x-hidden overflow-y-auto flex-1">
							{root.length === 0 ? (
								<p className="text-center py-4">
									<i className="fa-light fa-empty-set text-gray-500"></i>
								</p>
							) : (
								root.map((n) => (
									<div key={idKey(n.id)} className="border-b border-gray-100">
										<Item
											node={n}
											level={0}
											expandedIds={expandedIds}
											onToggle={onToggle}
											onPick={onPick}
											loadingIds={loadingIds}
											selected={selected}
											leafOnly={leafOnly}
										/>
									</div>
								))
							)}
						</div>
					</div>,
					document.body,
				)}
		</div>
	)
}

export default SingleTreeSelect
